import { formatDistanceToNow } from 'date-fns'
import { Calendar, FolderOpen, Loader2, MoreVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ExtractionScope } from '@/services/api/extraction-scope'
import type { LakeDocument } from '@/services/api/lake'
import { DetailCard, OutlineBadge, PageTitle } from './sections'

interface ImportsTabProps {
  document: LakeDocument
  scopes: ExtractionScope[]
  scopesLoading: boolean
  scopesError: string | null
  onDeleteScope: (scopeId: string) => Promise<void>
  onOpenExtractionModal: (opts: { wholeDoc?: boolean }) => void
}

export function ImportsTab({
  scopes,
  scopesLoading,
  scopesError,
  onDeleteScope,
  onOpenExtractionModal,
}: ImportsTabProps) {
  return (
    <div
      className="max-md:!px-4"
      style={{
        padding: '20px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <PageTitle>Imports</PageTitle>

      {/* Info card — explains the concept. The CTA lives in the header (when
          there are imports) or as a centered button inside the empty state
          (when there aren't). No duplicate button here. */}
      <DetailCard>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--foreground)',
            marginBottom: 6,
          }}
        >
          What is an import?
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.6,
            color: 'var(--muted-foreground)',
          }}
        >
          Importing a lake document copies it into a case. You can import the whole document or a
          specific page range. The imported document then appears in the case's documents list,
          where you can run extraction manually.
        </p>
      </DetailCard>

      {scopesLoading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 0',
          }}
        >
          <Loader2
            className="animate-spin"
            style={{ width: 22, height: 22, color: 'var(--muted-foreground)' }}
          />
        </div>
      ) : scopesError ? (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: '8px',
            border: '1px solid var(--destructive)',
            background: 'var(--destructive-surface)',
            color: 'var(--destructive)',
            fontSize: 12.5,
          }}
        >
          {scopesError}
        </div>
      ) : scopes.length === 0 ? (
        <DetailCard padding="40px 24px">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 4,
            }}
          >
            <FolderOpen
              size={44}
              style={{ color: 'var(--muted-foreground)', opacity: 0.4, marginBottom: 8 }}
            />
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--foreground)',
              }}
            >
              No imports yet
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: 'var(--muted-foreground)',
              }}
            >
              Import this document into a case to start extracting it.
            </p>
            <Button size="sm" className="mt-4" onClick={() => onOpenExtractionModal({})}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Import to case
            </Button>
          </div>
        </DetailCard>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {scopes.map(scope => {
            const scopeTone =
              scope.scope_type === 'all'
                ? 'accent'
                : scope.scope_type === 'tabular'
                  ? 'success'
                  : 'info'
            return (
              <DetailCard key={scope.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 14,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--foreground)',
                        }}
                      >
                        {scope.case_name}
                      </h4>
                      <OutlineBadge tone={scopeTone} mono>
                        {scope.scope_type === 'all'
                          ? 'ALL'
                          : scope.scope_type === 'tabular'
                            ? 'TABULAR'
                            : 'CHUNKS'}
                      </OutlineBadge>
                      {scope.scope_type === 'chunks' && scope.selected_chunk_ids && (
                        <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                          {scope.selected_chunk_ids.length} chunk
                          {scope.selected_chunk_ids.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {scope.scope_type === 'tabular' && scope.tabular_config && (
                        <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                          {scope.tabular_config.batches?.length ?? 0} batch
                          {(scope.tabular_config.batches?.length ?? 0) !== 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>

                    {scope.scope_note && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 12,
                          lineHeight: 1.55,
                          color: 'var(--muted-foreground)',
                          marginBottom: 8,
                        }}
                      >
                        {scope.scope_note}
                      </p>
                    )}

                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 11,
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      <Calendar size={11} />
                      {formatDistanceToNow(new Date(scope.created_at), { addSuffix: true })}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onDeleteScope(scope.id)}
                        className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Delete Scope
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </DetailCard>
            )
          })}
        </div>
      )}
    </div>
  )
}
